import { authFetch } from '@/utils/authFetch';

export interface VehicleSchedule {
  vehicleId: string;
  vehicleUnitNumber: string;
  lastDaily?: string;
  lastWeekly?: string;
  lastMonthly?: string;
  nextDailyDue?: string;
  nextWeeklyDue?: string;
  nextMonthlyDue?: string;
}

export type DueStatus = 'overdue' | 'due-today' | 'upcoming' | 'unknown';

/**
 * Determines overdue status by comparing against the actual due datetime (5pm).
 * This correctly handles the case where an inspection is due at 5pm today
 * but it's currently 2pm — it shows as 'due-today', not 'overdue'.
 */
export function getOverdueStatus(nextDue: string | undefined): DueStatus {
  if (!nextDue) return 'unknown';

  const now = new Date();
  const due = new Date(nextDue);

  // If current time is past the due time, it's overdue
  if (now > due) return 'overdue';

  // Check if due date falls on today (same calendar day)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);

  if (dueDay.getTime() === today.getTime()) return 'due-today';

  return 'upcoming';
}

export function formatDueDate(nextDue: string | undefined): string {
  if (!nextDue) return 'No record';

  const now = new Date();
  const due = new Date(nextDue);

  // Overdue — show how many days past
  if (now > due) {
    const diffMs = now.getTime() - due.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Due today at 9pm (overdue)';
    return `${diffDays}d overdue`;
  }

  // Due today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);

  if (dueDay.getTime() === today.getTime()) return 'Due today by 9pm';

  // Due tomorrow
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dueDay.getTime() === tomorrow.getTime()) return 'Due tomorrow by 9pm';

  // Due in N days
  const diffDays = Math.round((dueDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return `Due in ${diffDays}d`;
}

export async function getVehicleSchedule(vehicleId: string): Promise<VehicleSchedule | null> {
  try {
    const res = await authFetch(`/api/schedule/${vehicleId}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function getAllSchedules(): Promise<VehicleSchedule[]> {
  try {
    const res = await authFetch('/api/schedule');
    if (!res.ok) return [];
    return res.json();
  } catch {
    return [];
  }
}